<template>
    <div class="space-y-2">
        <h3 class="section-header">Documents</h3>
        <div
            v-if="documents.length > 0"
            class="space-y-2"
        >
            <a
                v-for="document in documents"
                :key="document.document_title"
                class="block"
                :href="document.document_closinglock_url"
                rel="noopener noreferrer"
                target="_blank"
            >
                <DsCardButton @click="handleDocumentClick(document)">
                    <div class="flex items-center gap-3">
                        <DsIcon
                            class="text-content-secondary"
                            :icon="documentIconMap.get(document.type) ?? FALLBACK_DOCUMENT_ICON"
                            size="lg"
                        />
                        <div class="flex-1 text-left">
                            <p class="text-sm font-medium text-content-primary">{{ document.document_title }}</p>
                        </div>
                    </div>
                </DsCardButton>
            </a>
        </div>
        <div
            v-else
            class="flex flex-col items-center gap-1 py-6"
        >
            <DsIcon
                class="text-ds-primary-500"
                icon="fa-regular fa-file-invoice-dollar"
                size="lg"
            />
            <div class="max-w-xs space-y-1 text-center">
                <h4 class="text-base font-semibold text-content-primary">No documents yet</h4>
                <p class="text-sm text-content-secondary">
                    Your documents will be available for download here as soon as they're ready.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsCardButton from '@/components/design-system/form/input/DsCardButton.vue';
import type { PayoffOrderDocument } from '@/interfaces/payoff/payoff-order.types';
import { PayoffOrderDocumentType } from '@/interfaces/payoff/payoff-order.types';
import { usePayoffOrderAnalyticsStore } from '@/stores/company/payoff-order-analytics.store';

interface PayoffOrderDocumentListProps {
    documents: PayoffOrderDocument[];
}

defineProps<PayoffOrderDocumentListProps>();

const { trackPayoffsEvent } = usePayoffOrderAnalyticsStore();

const FALLBACK_DOCUMENT_ICON = 'fa-regular fa-file-lines';
const documentIconMap = new Map<PayoffOrderDocumentType, string>([
    [PayoffOrderDocumentType.PAYOFF_STATEMENT, 'fa-regular fa-file-invoice-dollar'],
    [PayoffOrderDocumentType.VERIFICATION_RECEIPT, 'fa-regular fa-file-lines'],
    [PayoffOrderDocumentType.SELLER_AUTHORIZATION, 'fa-regular fa-file-contract'],
    [PayoffOrderDocumentType.UNKNOWN, FALLBACK_DOCUMENT_ICON],
]);

const handleDocumentClick = (document: PayoffOrderDocument) => {
    trackPayoffsEvent('File detail page - Side sheet - document clicked', {
        'Document name': document.document_title,
    });
};
</script>

<style scoped lang="scss">
.section-header {
    @apply text-xs font-semibold text-content-primary;
}
</style>
